import { api } from '../../api/client';
import type {
  ApiResponse, LoginRequest, LoginResponseData,
  VerifyChallengeRequest, VerifyChallengeResponseData,
  LoginConfig, LoginChallengeRequired, MfaEnrollAtLoginStartResponse,
} from './types';

/** Tiny wrapper that throws on the standard envelope's error path. */
async function unwrap<T>(p: Promise<{ data: ApiResponse<T> }>): Promise<T> {
  const { data } = await p;
  if (!data.success) throw new Error(data.error.message || 'Request failed');
  return data.data;
}

// ─── Login config (anonymous — before auth) ───────────────────────────────
/** GET /api/v1/auth/login-config — the global login mode for the login page
 *  (hint line).  Anonymous; silent on the global loader (page-load fetch). */
export async function getLoginConfig(): Promise<LoginConfig> {
  return unwrap(api.get<ApiResponse<LoginConfig>>(
    '/api/v1/auth/login-config', { silentLoading: true } as never));
}

// ─── Login (step 1 — credentials) ─────────────────────────────────────────
/** POST /api/v1/auth/login — credentials only.
 *  Returns either a completed login (no 2FA/MFA) or a challenge requirement. */
export async function login(req: LoginRequest): Promise<LoginResponseData> {
  return unwrap(api.post<ApiResponse<LoginResponseData>>('/api/v1/auth/login', req));
}

// ─── G6 SSO (handoff from ALPACA LIMS — one-time token, no password) ──────
/** POST /api/v1/auth/sso — exchanges the `?tkn=` one-time token minted by the
 *  ALPACA LIMS G6 header button for a JWT. Single-use, 2-minute expiry; on
 *  failure the caller falls back to the normal login page. */
export async function ssoLogin(tkn: string): Promise<VerifyChallengeResponseData> {
  return unwrap(api.post<ApiResponse<VerifyChallengeResponseData>>(
    '/api/v1/auth/sso', { tkn }));
}

// ─── Login (step 2 — verify OTP/TOTP) ─────────────────────────────────────
/** POST /api/v1/auth/challenge/verify — exchanges the 6-digit code for a JWT. */
export async function verifyChallenge(req: VerifyChallengeRequest): Promise<VerifyChallengeResponseData> {
  return unwrap(api.post<ApiResponse<VerifyChallengeResponseData>>(
    '/api/v1/auth/challenge/verify', req));
}

/** POST /api/v1/auth/2fa/resend — gets a fresh OTP for the SAME login attempt. */
export async function resendTwoFactor(challengeId: string): Promise<{
  challengeId: string; channel: 'Mobile' | 'Email'; maskedDestination: string; expiresInSeconds: number;
}> {
  return unwrap(api.post('/api/v1/auth/2fa/resend', { challengeId }));
}

/** POST /api/v1/auth/2fa/select-destination — ALPACA parity: an admin/service
 *  user under a FORCED 'twofactor' login mode picks which registered address
 *  gets the OTP (ported from TwoFactorAuthNewViewController's destination
 *  dropdown). Sends the OTP to the chosen destination and returns a normal
 *  2FA challenge. */
export async function selectTwoFactorDestination(
  pickId: string, index: number, rememberMe: boolean,
): Promise<LoginChallengeRequired> {
  return unwrap(api.post<ApiResponse<LoginChallengeRequired>>(
    '/api/v1/auth/2fa/select-destination', { pickId, index, rememberMe }));
}

// ─── MFA enroll-AT-LOGIN (anonymous — no JWT yet) ─────────────────────────
/** POST /api/v1/auth/mfa/enroll-at-login/start — "Add another authenticator"
 *  from an already-enrolled account's normal MFA challenge screen (e.g. a
 *  shared "service" login where a different person wants to add their own
 *  device). Returns a fresh secret/QR + a new "MfaEnroll" challengeId. */
export async function startMfaEnrollAtLogin(challengeId: string): Promise<MfaEnrollAtLoginStartResponse> {
  return unwrap(api.post<ApiResponse<MfaEnrollAtLoginStartResponse>>(
    '/api/v1/auth/mfa/enroll-at-login/start', { challengeId }));
}

/** POST /api/v1/auth/mfa/enroll-at-login/verify — BOTH persists the new
 *  secret AND completes login (used for first-time setup returned directly
 *  from /login, and for "Add another authenticator").
 *  `label` names the member for THIS device — stored on the secret so a shared
 *  account (e.g. "service") can attribute an e-signature to the real signer. */
export async function verifyMfaEnrollAtLogin(
  challengeId: string, code: string, rememberMe: boolean, label?: string,
): Promise<VerifyChallengeResponseData> {
  return unwrap(api.post<ApiResponse<VerifyChallengeResponseData>>(
    '/api/v1/auth/mfa/enroll-at-login/verify',
    { challengeId, code, rememberMe, label: label?.trim() || null }));
}
